import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { SiteHeader } from "@/components/site-header"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { Input } from "@/components/ui/input"
import { SearchIcon } from "lucide-react"
import MilestoneKanban, {
  type ShipmentMilestoneRow,
  type MilestoneDef,
} from "./MilestoneKanban"

// ─── Milestones ───────────────────────────────────────────────────────────────

const NACIONAL_MILESTONES: MilestoneDef[] = [
  { key: "originArrivalAt",        label: "Llegada al origen",   short: "Llegada origen" },
  { key: "originLoadingAt",        label: "Carga en origen",     short: "Carga" },
  { key: "originDepartureAt",      label: "Salida de origen",    short: "Salida origen" },
  { key: "destinationArrivalAt",   label: "Llegada al destino",  short: "Llegada destino" },
  { key: "destinationUnloadingAt", label: "Descarga en destino", short: "Descarga" },
]

const QUERY_KEY = ["shipments", "nacional"] as const

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CargasNacionalPage() {
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const [search, setSearch] = useState("")

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery<ShipmentMilestoneRow[]>({
    queryKey: QUERY_KEY,
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments?workLine=nacional&deleted=false&pageSize=500`)
      const json = await res.json() as { data: ShipmentMilestoneRow[] }
      return json.data
    },
  })

  // ── Filter ───────────────────────────────────────────────────────────────────
  const items = useMemo<ShipmentMilestoneRow[]>(() => {
    if (!data) return []
    const active = data.filter(r => !r.deletedAt)
    if (!search.trim()) return active
    const term = search.toLowerCase().trim()
    return active.filter(r =>
      r.senderName?.toLowerCase().includes(term) ||
      r.recipientName?.toLowerCase().includes(term) ||
      r.clientName?.toLowerCase().includes(term) ||
      r.driverNames?.toLowerCase().includes(term) ||
      String(r.internalId).includes(term),
    )
  }, [data, search])

  // ── Mutation ─────────────────────────────────────────────────────────────────
  const milestoneMut = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, string | null> }) => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(updates),
      })
      return res.json()
    },
    onError: (_err: Error) => {
      toast.error("Error al actualizar el hito")
      // Revert optimistic update
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  // ── Milestone change handler (optimistic) ────────────────────────────────────
  function handleMilestoneChange(id: string, updates: Record<string, string | null>) {
    // Apply immediately to the cache for instant visual feedback
    qc.setQueryData<ShipmentMilestoneRow[]>(QUERY_KEY, prev =>
      prev ? prev.map(r => r.id === id ? { ...r, ...updates } : r) : prev,
    )
    milestoneMut.mutate({ id, updates })
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <SiteHeader title="Cargas — Nacional" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6 space-y-4">

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-72">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar remitente, destinatario, cliente..."
              className="pl-8"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span className="text-xs text-muted-foreground">
            {isLoading ? "Cargando..." : `${items.length} envío${items.length !== 1 ? "s" : ""}`}
          </span>
        </div>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground py-24">
            Cargando cargas...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground py-24">
            No se encontraron cargas nacionales.
          </div>
        ) : (
          <MilestoneKanban
            items={items}
            milestones={NACIONAL_MILESTONES}
            onView={id => navigate(`/shipments/${id}`)}
            onMilestoneChange={handleMilestoneChange}
          />
        )}

      </div>
    </>
  )
}
